import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import {
  TiendaPerfectaVisitHistoryDay,
  TiendaPerfectaVisitHistoryKpis,
  TiendaPerfectaVisitHistoryNamed,
  TiendaPerfectaVisitHistoryStop,
  TiendaPerfectaVisitHistoryVan,
} from 'models/TiendaPerfecta';

import { compliancePct } from './compliance';
import { MaterialTotal } from './materials';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Lo que el usuario eligió resaltar; gobierna todos los bloques. */
export type VisitSelection =
  | { kind: 'day'; date: string }
  | { kind: 'route'; routeId: string; date: string }
  | { kind: 'van'; vanId: string }
  | { kind: 'zone'; zoneId: string }
  | null;

export const stopMatchesSelection = (
  stop: TiendaPerfectaVisitHistoryStop,
  selection: VisitSelection
): boolean => {
  if (!selection) return true;

  switch (selection.kind) {
    case 'day':
      return stop.plannedDate === selection.date;
    case 'route':
      return (
        stop.route.id === selection.routeId &&
        stop.plannedDate === selection.date
      );
    case 'van':
      return stop.van.id === selection.vanId;
    case 'zone':
      return stop.zone.id === selection.zoneId;
  }
};

export interface AggregatedExecution {
  routeId: string;
  routeCode: string;
  vanId: string;
  date: string;
  zone: TiendaPerfectaVisitHistoryNamed;
  planned: number;
  executed: number;
  incidents: number;
  pending: number;
  compliancePct: number;
  materialUnits: number;
  startedAt: string | null;
  finishedAt: string | null;
  durationMinutes: number | null;
  visitsPerHour: number | null;
}

export interface AggregatedTimeStats {
  /** `HH:mm` en el huso pedido. */
  avgStartTime: string | null;
  avgEndTime: string | null;
  avgDurationMinutes: number | null;
  visitsPerHour: number | null;
}

export interface AggregatedTotals extends AggregatedTimeStats {
  routes: number;
  days: number;
  planned: number;
  executed: number;
  incidents: number;
  pending: number;
  compliancePct: number;
  materialUnits: number;
}

export interface AggregatedVan extends AggregatedTotals {
  van: TiendaPerfectaVisitHistoryVan;
  zones: TiendaPerfectaVisitHistoryNamed[];
  executions: AggregatedExecution[];
}

export interface AggregatedMaterial extends MaterialTotal {
  name: string;
}

export interface AggregatedStops {
  kpis: TiendaPerfectaVisitHistoryKpis;
  byDay: TiendaPerfectaVisitHistoryDay[];
  materialTotals: AggregatedMaterial[];
  vans: AggregatedVan[];
  executions: AggregatedExecution[];
  /** Misma métrica que una fila de camioneta, sobre TODAS las ejecuciones. */
  total: AggregatedTotals;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

/**
 * Minutos desde la medianoche de la FECHA PLANIFICADA de la ejecucion, en el
 * huso pedido. Una hora que cae al dia siguiente supera 1440 (00:30 -> 1470),
 * asi el promedio no parte la noche en dos.
 */
const minutesSincePlannedMidnight = (
  iso: string,
  plannedDate: string,
  tz: string
): number =>
  Math.round(
    dayjs(iso).diff(dayjs.tz(plannedDate, tz).startOf('day'), 'minute', true)
  );

const clock = (minutes: number): string => {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;

  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(
    m % 60
  ).padStart(2, '0')}`;
};

const mean = (values: number[]): number | null =>
  values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;

/**
 * Promedios sobre las ejecuciones (no promedio de promedios). Una ejecución sin
 * paradas iniciadas no entra a los tiempos, pero sí a los conteos. La velocidad
 * es ejecutadas / horas totales, ambas de las ejecuciones con duración.
 * Inicio y término se miden desde la medianoche de la fecha planificada de
 * cada ejecución (ver `minutesSincePlannedMidnight`) y el promedio se formatea
 * módulo 24 h.
 */
const timeStats = (
  executions: AggregatedExecution[],
  tz: string
): AggregatedTimeStats => {
  const avgStart = mean(
    executions
      .filter((e) => e.startedAt)
      .map((e) =>
        minutesSincePlannedMidnight(e.startedAt as string, e.date, tz)
      )
  );
  const avgEnd = mean(
    executions
      .filter((e) => e.finishedAt)
      .map((e) =>
        minutesSincePlannedMidnight(e.finishedAt as string, e.date, tz)
      )
  );
  const timed = executions.filter((e) => e.durationMinutes !== null);
  const totalMinutes = timed.reduce((s, e) => s + (e.durationMinutes ?? 0), 0);
  const avgDuration = mean(timed.map((e) => e.durationMinutes as number));

  return {
    avgStartTime: avgStart === null ? null : clock(avgStart),
    avgEndTime: avgEnd === null ? null : clock(avgEnd),
    avgDurationMinutes: avgDuration === null ? null : Math.round(avgDuration),
    visitsPerHour:
      totalMinutes > 0
        ? round1(
            timed.reduce((s, e) => s + e.executed, 0) / (totalMinutes / 60)
          )
        : null,
  };
};

const sumOf = (
  executions: AggregatedExecution[],
  key: 'planned' | 'executed' | 'incidents' | 'pending' | 'materialUnits'
): number => executions.reduce((s, e) => s + e[key], 0);

const totalsOf = (
  executions: AggregatedExecution[],
  tz: string
): AggregatedTotals => {
  const planned = sumOf(executions, 'planned');
  const executed = sumOf(executions, 'executed');

  return {
    routes: executions.length,
    days: new Set(executions.map((e) => e.date)).size,
    planned,
    executed,
    incidents: sumOf(executions, 'incidents'),
    pending: sumOf(executions, 'pending'),
    compliancePct: compliancePct(executed, planned),
    materialUnits: sumOf(executions, 'materialUnits'),
    ...timeStats(executions, tz),
  };
};

const buildExecution = (
  stops: TiendaPerfectaVisitHistoryStop[]
): AggregatedExecution => {
  const first = stops[0];
  const executedStops = stops.filter((s) => s.outcome === 'executed');
  const starts = stops.map((s) => s.startedAt).filter(Boolean) as string[];
  const ends = stops.map((s) => s.finishedAt).filter(Boolean) as string[];
  const startedAt = starts.length
    ? starts.reduce((a, b) => (dayjs(a).isBefore(dayjs(b)) ? a : b))
    : null;
  const finishedAt = ends.length
    ? ends.reduce((a, b) => (dayjs(a).isAfter(dayjs(b)) ? a : b))
    : null;
  const durationMinutes =
    startedAt && finishedAt
      ? Math.round(dayjs(finishedAt).diff(dayjs(startedAt), 'minute', true))
      : null;
  const executed = executedStops.length;

  return {
    routeId: first.route.id,
    routeCode: first.route.code,
    vanId: first.van.id,
    date: first.plannedDate,
    zone: first.zone,
    planned: stops.length,
    executed,
    incidents: stops.filter((s) => s.outcome === 'incident').length,
    pending: stops.filter((s) => s.outcome === 'pending').length,
    compliancePct: compliancePct(executed, stops.length),
    // Material rule: every stop with recorded deliveries counts, whatever its
    // status (pending, in progress, finished, incident). It mirrors the
    // service criterion "delivered material is delivered".
    materialUnits: stops.reduce(
      (s, st) => s + st.materials.reduce((u, m) => u + m.units, 0),
      0
    ),
    startedAt,
    finishedAt,
    durationMinutes,
    visitsPerHour:
      durationMinutes && durationMinutes > 0
        ? round1(executed / (durationMinutes / 60))
        : null,
  };
};

const groupBy = <T>(
  items: T[],
  keyOf: (item: T) => string
): Map<string, T[]> => {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const key = keyOf(item);
    const list = groups.get(key);
    if (list) list.push(item);
    else groups.set(key, [item]);
  });

  return groups;
};

/**
 * Recalcula KPIs, por día, material, consolidado por camioneta y filas por
 * ejecución desde la lista plana de paradas. Es la única fuente de la
 * selección: todos los bloques leen de acá, así los totales siempre cuadran.
 */
export const aggregateStops = (
  stops: TiendaPerfectaVisitHistoryStop[],
  tz: string
): AggregatedStops => {
  const executions = [
    ...groupBy(stops, (s) => `${s.route.id}|${s.plannedDate}`).values(),
  ]
    .map(buildExecution)
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) || a.routeCode.localeCompare(b.routeCode)
    );

  const totals = totalsOf(executions, tz);

  const byDay = [...groupBy(executions, (e) => e.date).entries()]
    .map(([date, list]) => ({
      date,
      planned: sumOf(list, 'planned'),
      executed: sumOf(list, 'executed'),
      incidents: sumOf(list, 'incidents'),
      pending: sumOf(list, 'pending'),
      routes: list.length,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const materialMap = new Map<string, AggregatedMaterial>();
  // Same rule as per execution: every stop with recorded deliveries counts
  // regardless of status ("delivered material is delivered", as the service).
  stops.forEach((s) =>
    s.materials.forEach((m) => {
      const prev = materialMap.get(m.materialId);
      materialMap.set(m.materialId, {
        materialId: m.materialId,
        name: m.name,
        units: (prev?.units ?? 0) + m.units,
      });
    })
  );
  const materialTotals = [...materialMap.values()].sort(
    (a, b) => b.units - a.units || a.name.localeCompare(b.name)
  );

  const stopsByVan = groupBy(stops, (s) => s.van.id);
  const vans = [...groupBy(executions, (e) => e.vanId).entries()]
    .map(([vanId, list]): AggregatedVan => {
      const vanStops = stopsByVan.get(vanId) ?? [];
      const zones = new Map<string, TiendaPerfectaVisitHistoryNamed>();
      list.forEach((e) => zones.set(e.zone.id, e.zone));

      return {
        van: vanStops[0].van,
        zones: [...zones.values()].sort((a, b) => a.name.localeCompare(b.name)),
        executions: list,
        ...totalsOf(list, tz),
      };
    })
    .sort((a, b) => a.van.name.localeCompare(b.van.name));

  return {
    kpis: {
      planned: totals.planned,
      executed: totals.executed,
      incidents: totals.incidents,
      pending: totals.pending,
      compliancePct: totals.compliancePct,
      routes: totals.routes,
      materialUnits: totals.materialUnits,
    },
    byDay,
    materialTotals,
    vans,
    executions,
    total: totals,
  };
};
